'use client';
import { Alert, Button, Checkbox, Col, Divider, Form, Input, message, Modal, Row, Spin } from 'antd';
import styles from './desktopForm.module.scss';
import { useNavigate, useParams } from 'react-router-dom';
import CustomUploadComp from '../upload/upload.index';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import CategorySelectComp from './category/category.index';
import { COLORS } from 'utilities';
import LocationSelectComp from './location/location.index';
import AdMapComp from '../map/map.index';
import dynamic from 'next/dynamic';
import { APPlicationStoreEnum, CategoryFieldType, PaginationType, PaymentTypeEnum, PostType } from 'types';
import { AppStorage, Commafy, RestAPI, useAsyncClick, usePopstateBlocker } from 'scripts';
import { API, PATH } from 'data';
import CategoryFieldValuesComp from '../categoryFieldValues/categoryFieldValues.index';
import SocialMediaComp from '../socialMedia/socialMedia.index';
import { __ConvertCFVForAPI } from '../categoryFieldValues/categoryFieldValues.script';
import { TbCreditCard } from 'react-icons/tb';
import { GlobalContext } from 'context';
import { __UpdateLastPostLocation } from '../addPost.script';
import { __PostFormLocationType } from '../addPost.type';

function PostDesktopFormComp(props: { data?: PostType<'detail'> }) {
    const nav = useNavigate();
    const { profile } = useContext(GlobalContext);
    const { allowBack, blockBack, comp } = usePopstateBlocker();
    const Map = useMemo(
        () =>
            dynamic(() => import('components/locationPicker/locationPicker.index'), {
                loading: () => (
                    <div className={`w-100 pv-9 center-content flex-1`}>
                        <Spin />
                    </div>
                ),
                ssr: false,
            }),
        [],
    );
    const { slug } = useParams();
    const [form] = Form.useForm();
    const category = Form.useWatch('category', form);
    const [categoryFields, setCategoryFields] = useState<CategoryFieldType<'detail'>[]>();
    const price = useRef(0);
    const [selectedLocations, setSelectedLocations] = useState<__PostFormLocationType | undefined>(
        props.data?.locations?.at(0) ?? undefined,
    );
    const [paymentModal, setPaymentModal] = useState<number>();
    const [uploading, setUploading] = useState(false);
    const [hasImages, setHasImages] = useState(false);
    const lastPostLocation = AppStorage.lastPostLocation.get();

    useEffect(() => {
        setCategoryFields(undefined);
        const cat = props.data?.category.id ?? category;
        if (cat) {
            RestAPI.get<PaginationType<CategoryFieldType<'detail'>>>(API.categoryField.list, {
                category_ancestors: cat,
                page_size: 100,
            }).then((res) => setCategoryFields(res.data.data));
        }
    }, [category]);

    const submitHandler = () => {
        return new Promise<{ id: number; slug: string }>((resolve, reject) => {
            const values = form.getFieldsValue();
            const request = slug ? RestAPI.put : RestAPI.post;
            const url = slug ? API.posts.update(slug) : API.posts.create;
            request<PostType<'detail'>>(url, {
                ...values,
                gallery: values.gallery?.filter((i: number) => i !== 0),
                category_fields_values: undefined,
                locations: props.data
                    ? props.data.locations?.map((i) => i.id)
                    : values.locations.map((i: __PostFormLocationType) => i.id),
            })
                .then((res) => {
                    __UpdateLastPostLocation(values);
                    RestAPI.post(
                        API.categoryField.createBulkValues,
                        __ConvertCFVForAPI(values.category_fields_values, res.data.slug, categoryFields),
                    )
                        .then(() => {
                            allowBack();
                            message.success(
                                `${slug ? 'ویرایش' : 'ساخت'} آگهی با موفقیت انجام شد و پس از تایید مدیریت در لیست آگهی های تیک سبز قرار خواهد گرفت.`,
                            );
                            resolve({ id: res.data.id, slug: res.data.slug });
                            if (slug) nav(-1);
                        })
                        .catch(() => {
                            message.error(
                                `فرآیند ${slug ? 'ویرایش' : 'ساخت'} آگهی با خطا مواجه شد. لطفا بعدا تلاش کنید.`,
                            );
                            reject();
                        });
                })
                .catch((e) => {
                    if (JSON.stringify(e).includes('This object can only be updated three times a day.'))
                        message.error(
                            `شما امکان ویرایش آگهی بیش از ۳ مرتبه در روز را ندارید. لطفا تا پایان محدودیت ویرایش آگهی صبر کنید و یا از با پشتیبانی ارتباط بگیرید`,
                        );
                    else message.error(`فرآیند ${slug ? 'ویرایش' : 'ساخت'} آگهی با خطا مواجه شد. لطفا بعدا تلاش کنید.`);
                    reject();
                });
        });
    };

    const onPaymentHandler = useAsyncClick(() => {
        return new Promise((resolve, reject) => {
            submitHandler().then((post) => {
                if (AppStorage.applicationStore.get() === APPlicationStoreEnum.cafebazar) {
                    onBazarPaymentHandler(post)
                        .then(() => resolve(true))
                        .catch(() => reject());
                } else {
                    onZarinPalPaymentHandler(post)
                        .then(() => resolve(true))
                        .catch(() => reject());
                }
            });
        });
    });

    const onZarinPalPaymentHandler = (post: { id: number; slug: string }) => {
        return new Promise(() => {
            RestAPI.post<{ url: string }>(API.payment.pay, {
                type: PaymentTypeEnum.create,
                post: post.id,
            })
                .then((res) => (window.location.href = res.data.url))
                .catch(() => {
                    message.error('ورود به درگاه پرداخت با خطا مواجه شد. لطفا بعدا تلاش کنید');
                    nav(PATH.panel.myAds.edit(post.slug));
                });
        });
    };

    const onBazarPaymentHandler = (post: { id: number; slug: string }) => {
        return new Promise(() => {
            RestAPI.post<{ payment_url: string }>(API.payment.bazarPay, {
                type: PaymentTypeEnum.create,
                post: post.id,
            })
                .then((res) => (window.location.href = res.data.payment_url))
                .catch(() => {
                    message.error('ورود به درگاه پرداخت با خطا مواجه شد. لطفا بعدا تلاش کنید');
                    nav(PATH.panel.myAds.edit(post.slug));
                });
        });
    };
    const uniqueClassGen = (name: string) => {
        return `form-item-id-${name}`;
    };

    const finishHandler = useAsyncClick(() => {
        const values = form.getFieldsValue();
        if (categoryFields === undefined) {
            message.error(`خطا در بارگذاری مشخصات فنی. لطفا مشخصات فنی را تکمیل فرمایید`);
            return;
        }
        if (!values.chatable && !values.has_phone) {
            message.error(`آگهی شما بایستی یکی از قابلیت های چت و یا تماس را داشته باشد`);
            return;
        }
        if (!values.gallery?.filter((i: number) => i !== 0).length) {
            message.error(`آگهی شما بایستی حداقل یک فایل تصویری داشته باشد`);
            return;
        }
        if (!hasImages) {
            message.error(`آگهی شما بایستی حداقل یک عکس داشته باشد`);
            return;
        }
        if (uploading) {
            message.error(`فایل مورد نظر شما در حال آپلود میباشد. لطفا صبر کنید`);
            return;
        }
        if (slug) return submitHandler();
        else {
            allowBack();
            setPaymentModal(price.current * (selectedLocations?.coefficient ?? 1));
        }
    });

    return (
        <>
            <Form
                form={form}
                layout="vertical"
                requiredMark={false}
                onFinishFailed={(e) => {
                    const id = uniqueClassGen(e.errorFields.at(0)?.name?.toString() ?? '');
                    if (id?.length) {
                        const element = document.querySelector('.' + id.toString());
                        if (element) {
                            element.scrollIntoView({ behavior: 'smooth', block: 'center' });
                        }
                    }
                }}
                onFieldsChange={(e) => {
                    if (e.some((i) => i.touched)) blockBack();
                    else allowBack();
                }}
                // initialValues={slug ? __ConvertAPIDataToForm(initialPost) : { chatable: true }}
                onFinish={finishHandler.onClick}
                // onValuesChange={() => formValues.set(form.getFieldsValue())}
            >
                <div className={`${styles['form-container']} d-flex g-4`}>
                    <div className={`${styles['right-form-section']}`}>
                        <Row gutter={24}>
                            <Col lg={24} md={{ span: 24 }} xs={{ span: 24 }}>
                                <div className="d-flex flex-between mb-5">
                                    <h2 className="t-h2 t-l-black1 t-500">ثبت آگهی</h2>
                                    {/* <h3 className="t-h3 t-primary-i t-400 center-content g-1 pointer">
                                <IoIosHelpCircleOutline size={20} className="t-primary" /> راهنمای ثبت آگهی
                            </h3> */}
                                </div>
                            </Col>
                            {!props.data ? (
                                <Col lg={{ span: 12 }} md={{ span: 24 }} xs={{ span: 24 }}>
                                    <Form.Item
                                        name="category"
                                        className={uniqueClassGen('category')}
                                        label={<p className="t-h3 t-400 t-l-black1">{'دسته بندی'}</p>}
                                        rules={[
                                            {
                                                required: true,
                                                message: <span>لطفا دسته بندی آگهی خود را مشخص کنید!</span>,
                                            },
                                        ]}
                                    >
                                        <CategorySelectComp
                                            placeholder="لطفا دسته بندی خود را انتخاب کنید"
                                            className="d-pre-md-none-i"
                                            onPriceChange={(e) => {
                                                price.current = e / 10;
                                            }}
                                        />
                                    </Form.Item>
                                </Col>
                            ) : null}
                            <Col lg={{ span: 12 }} md={{ span: 24 }} xs={{ span: 24 }}>
                                <Form.Item
                                    name="title"
                                    className={uniqueClassGen('title')}
                                    label={<p className="t-h3 t-400 t-l-black1">{'عنوان آگهی'}</p>}
                                    rules={[
                                        { required: true, message: <span>لطفا عنوان آگهی خود را وارد کنید!</span> },
                                    ]}
                                    initialValue={props.data?.title}
                                >
                                    <Input placeholder="عنوان آگهی خود را بنویسید..." showCount maxLength={50} />
                                </Form.Item>
                            </Col>
                            <Col lg={{ span: 24 }} md={{ span: 24 }} xs={{ span: 24 }}>
                                <Form.Item
                                    name="description"
                                    className={uniqueClassGen('description')}
                                    label={<p className="t-h3 t-400 t-l-black1">{'توضیحات آگهی'}</p>}
                                    rules={[{ required: true, message: <span>لطفا توضیحات را وارد کنید!</span> }]}
                                    initialValue={props.data?.description}
                                >
                                    <Input.TextArea color={COLORS.black} placeholder="توضیحات آگهی خود را بنویسید..." />
                                </Form.Item>
                            </Col>
                            {!props.data ? (
                                <Col lg={{ span: 12 }} md={{ span: 24 }} xs={{ span: 24 }}>
                                    <Form.Item
                                        name="locations"
                                        className={uniqueClassGen('locations')}
                                        label={<p className="t-h3 t-400 t-l-black1">{'موقعیت مکانی'}</p>}
                                        initialValue={
                                            lastPostLocation?.location
                                                ? [
                                                      {
                                                          id: lastPostLocation.location.id,
                                                          title: lastPostLocation.location.title,
                                                      },
                                                  ]
                                                : undefined
                                        }
                                        rules={[
                                            {
                                                required: true,
                                                message: <span>لطفا موقعیت مکانی خود را انتخاب کنید!</span>,
                                            },
                                        ]}
                                    >
                                        <LocationSelectComp
                                            placeholder="لطفا موقعیت مکانی خود را انتخاب کنید"
                                            defaultValue={
                                                lastPostLocation?.location ? [lastPostLocation.location] : undefined
                                            }
                                            altOnChange={setSelectedLocations}
                                        />
                                    </Form.Item>
                                </Col>
                            ) : null}
                            <Col lg={{ span: 24 }} md={{ span: 24 }} xs={{ span: 24 }}>
                                <Form.Item
                                    name="latitude"
                                    className="d-none"
                                    initialValue={
                                        selectedLocations?.latitude ??
                                        (!props.data ? lastPostLocation?.latitude : undefined)
                                    }
                                />
                                <Form.Item
                                    name="longitude"
                                    className="d-none"
                                    initialValue={
                                        selectedLocations?.longitude ??
                                        (!props.data ? lastPostLocation?.longitude : undefined)
                                    }
                                />
                                <AdMapComp
                                    form={form}
                                    Map={Map}
                                    initialValue={
                                        selectedLocations?.latitude && selectedLocations.longitude
                                            ? [selectedLocations.latitude, selectedLocations.longitude]
                                            : !props.data && lastPostLocation?.latitude && lastPostLocation.longitude
                                              ? [lastPostLocation.latitude, lastPostLocation.longitude]
                                              : undefined
                                    }
                                />
                            </Col>
                            <Col lg={{ span: 24 }} md={{ span: 24 }} xs={{ span: 24 }}>
                                <Form.Item
                                    name="address"
                                    className={uniqueClassGen('address')}
                                    label={<p className="t-h3 t-400 t-l-black1">{'آدرس'}</p>}
                                    rules={[{ required: true, message: <span>لطفا آدرس را وارد کنید!</span> }]}
                                    initialValue={
                                        props.data?.address ?? (!props.data ? lastPostLocation?.address : undefined)
                                    }
                                >
                                    <Input.TextArea color={COLORS.black} placeholder="آدرس آگهی خود را بنویسید..." />
                                </Form.Item>
                            </Col>
                            <Col lg={{ span: 12 }} md={{ span: 24 }} xs={{ span: 24 }}>
                                <Form.Item
                                    name="chatable"
                                    className={uniqueClassGen('chatable')}
                                    valuePropName="checked"
                                    label={<p className="t-h3 t-400 t-l-black1">{'چت'}</p>}
                                    initialValue={props.data?.chatable}
                                >
                                    <Checkbox>
                                        <p>چت برای این آگهی فعال است</p>
                                    </Checkbox>
                                </Form.Item>
                            </Col>
                            <Col lg={{ span: 24 }} md={{ span: 24 }} xs={{ span: 24 }}>
                                <Form.Item
                                    name={'has_phone'}
                                    className={uniqueClassGen('has_phone')}
                                    label={'تماس با ' + profile?.phone}
                                    valuePropName={'checked'}
                                    initialValue={props.data?.has_phone}
                                >
                                    <Checkbox>
                                        <p>قابلیت تماس برای این آگهی فعال است</p>
                                    </Checkbox>
                                </Form.Item>
                            </Col>
                            {categoryFields?.length ? (
                                <>
                                    <Divider />
                                    <Col
                                        lg={{ span: 24 }}
                                        md={{ span: 24 }}
                                        xs={{ span: 24 }}
                                        className={uniqueClassGen('category_fields_values')}
                                    >
                                        {/* <Form.Item
                                            name={'category_fields_values'}
                                            initialValue={props.data?.category_fields_values}
                                        > */}
                                        <CategoryFieldValuesComp
                                            categoryFields={categoryFields}
                                            initialData={props.data?.category_fields_values}
                                        />
                                        {/* </Form.Item> */}
                                    </Col>
                                </>
                            ) : null}
                            <Col
                                lg={{ span: 24 }}
                                md={{ span: 24 }}
                                xs={{ span: 24 }}
                                className={uniqueClassGen('socials')}
                            >
                                <SocialMediaComp initialData={props.data?.socials} />
                            </Col>
                        </Row>
                        <Alert
                            className="mt-5 bg-d-bgDark"
                            message="شرایط ثبت آگهی در سایت تیک سبز"
                            description={
                                <div className="p-3">
                                    <p className="t-h4">
                                        ۱- تصاویر آگهی باید واضح، واقعی و با کیفیت مناسب باشند و تصویر اول فاقد هرگونه
                                        نوشته،ایموجی یا متن اضافه باشد
                                    </p>
                                    <p className="t-h4">۲- درج قیمت محصول الزامی است.</p>
                                    <p className="t-h4">
                                        ۳- آدرس فروشگاه، کارگاه، تولیدی یا محل ارائه خدمات باید به‌صورت کامل و دقیق در
                                        متن آگهی درج شود.
                                    </p>
                                    <p className="t-h4">
                                        ۴- استفاده از عبارات تبلیغاتی اغراق‌آمیز در عنوان آگهی مانند «حراجی»، «تخفیف
                                        ویژه»، «زیر قیمت بازار» و موارد مشابه مجاز نیست.
                                    </p>
                                    <p className="t-h4">
                                        ۵- ثبت آگهی مربوط به کالاهای شخصی یا دست‌دوم در تیک سبز مجاز نیست. این پلتفرم
                                        صرفاً برای معرفی فروشگاه‌ها، تولیدکنندگان و کسب‌وکارها طراحی شده است.
                                    </p>
                                    <p className="t-h4">
                                        ۶- ثبت آگهی مواد غذایی دست‌ساز یا خانگی، یا محصولاتی که فاقد تاریخ مصرف، مجوز یا
                                        مشخصات استاندارد هستند مجاز نیست.
                                    </p>
                                    <p className="t-h4">
                                        ۷- ثبت آگهی مربوط به انواع قرص‌ها، داروهای شیمیایی یا گیاهی، مکمل‌های چاقی و
                                        لاغری، افزایش قد، درمان زگیل و سایر محصولات دارویی یا شبه‌دارویی ممنوع است.
                                    </p>
                                    <ul>
                                        <li>
                                            مسئولیت صحت اطلاعات، متن آگهی، تصاویر، قیمت و مشخصات درج‌شده به‌طور کامل بر
                                            عهده آگهی‌دهنده است. و پلتفرم هیچ مسئولیتی در قبال آگهی شما ندارد.
                                        </li>
                                        <li>
                                            با ثبت آگهی در پلتفرم تیک سبز، شما تأیید می‌کنید که تمامی قوانین فوق را
                                            مطالعه کرده و پذیرفته‌اید.
                                        </li>
                                    </ul>
                                </div>
                            }
                            type="warning"
                        />
                    </div>
                    <div className={`${styles['left-form-section']}`}>
                        <div className={`${styles['left-content']}`}>
                            <Row gutter={24}>
                                <Col lg={{ span: 24 }}>
                                    <h3 className="t-h2 t-l-black1 t-500 mb-2">تصاویر آگهی</h3>
                                </Col>
                                <Col lg={{ span: 24 }} md={{ span: 24 }} xs={{ span: 24 }}>
                                    <Form.Item
                                        name={'gallery'}
                                        initialValue={props.data?.gallery?.map((i) => i.id) ?? []}
                                    >
                                        <CustomUploadComp
                                            initialValue={props.data?.gallery}
                                            uploadingChange={setUploading}
                                            onHasImagesChange={setHasImages}
                                            uploading={uploading}
                                        />
                                    </Form.Item>
                                </Col>
                            </Row>
                            <div className="g-2 center-content-y mt-2">
                                <Button htmlType="reset" className="w-100" onClick={() => form.resetFields()}>
                                    {slug ? 'تنظیم مجدد فرم' : 'پاکسازی فرم'}
                                </Button>
                                <Button className="w-100" htmlType="submit" loading={finishHandler.loading}>
                                    {slug ? 'ویرایش آگهی' : 'پرداخت امن'}
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>
            </Form>
            <Modal
                open={paymentModal !== undefined}
                onClose={() => setPaymentModal(undefined)}
                onCancel={() => setPaymentModal(undefined)}
                onOk={onPaymentHandler.onClick}
                okText="ورود به صفحه پرداخت"
                okButtonProps={{ loading: onPaymentHandler.loading, type: 'default' }}
                cancelButtonProps={{ type: 'primary' }}
                title={
                    <div className="align-items-center g-1">
                        <TbCreditCard className="t-l-black1 t-d-gray2" size={24} />
                        پرداخت هزینه آگهی
                    </div>
                }
            >
                <p>
                    هزینه انتشار آگهی شما توسط تیک سبز که بر اساس دسته‌بندی و موقعیت‌های مورد نظرتان محاسبه شده است
                    برابر است با :
                </p>
                <h4 className="mt-2 t-500 t-gray2-i mb-2">
                    {paymentModal ? Commafy(paymentModal) : ''} تومان برای{' '}
                    <span className="t-primary">اشتراک یک سال</span>
                </h4>
                <p className="t-l-black1 t-400">
                    آگهی شما پس از پرداخت و تایید توسط مدیریت به مدت ۱ سال در لیست آگهی های تیک سبز قرار خواهد گرفت.
                </p>
                <Alert
                    className="mv-3 bg-d-bgDark"
                    message="لطفا پیش از ورود به درگاه پرداخت از خاموش بودن فیلترشکن خود اطمینان حاصل کنید"
                    type="warning"
                    showIcon
                />
            </Modal>
            {comp}
        </>
    );
}

export default PostDesktopFormComp;
